import { useState, useMemo, useEffect, useRef } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"

export function HouseAffordabilityCalculator() {
    const [annualIncome, setAnnualIncome] = useState(120000)
    const [loanTerm, setLoanTerm] = useState(30)
    const [interestRate, setInterestRate] = useState(6.317)
    const [monthlyDebt, setMonthlyDebt] = useState(0)
    const [downPayment, setDownPayment] = useState(20)
    const [downPaymentMode, setDownPaymentMode] = useState("percentage")
    const [propertyTax, setPropertyTax] = useState(1.5)
    const [propertyTaxMode, setPropertyTaxMode] = useState("percentage")
    const [hoaFee, setHoaFee] = useState(0)
    const [hoaFeeMode, setHoaFeeMode] = useState("percentage")
    const [insurance, setInsurance] = useState(0.5)
    const [insuranceMode, setInsuranceMode] = useState("percentage")
    const [dtiRatio, setDtiRatio] = useState("conventional")

    // Debounced values
    const debouncedAnnualIncome = useDebounce(annualIncome, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedMonthlyDebt = useDebounce(monthlyDebt, 500)
    const debouncedDownPayment = useDebounce(downPayment, 500)
    const debouncedPropertyTax = useDebounce(propertyTax, 500)
    const debouncedHoaFee = useDebounce(hoaFee, 500)
    const debouncedInsurance = useDebounce(insurance, 500)

    // DTI ratio options
    const dtiOptions = [
        { value: "conventional", label: "Conventional loan (28/36 rule)" },
        { value: "fha", label: "FHA loan (31/43 rule)" },
        ...Array.from({ length: 12 }, (_, i) => {
            const percentage = (i + 1) * 5; // 10, 20, 30, ..., 80
            return { value: String(percentage), label: `${percentage}%` };
        }),
    ]

    // Calculate DTI limits based on selected option
    const dtiLimits = useMemo(() => {
        switch (dtiRatio) {
            case "conventional":
                return { frontEnd: 0.28, backEnd: 0.36 }
            case "fha":
                return { frontEnd: 0.31, backEnd: 0.43 }
            default:
                // If it's a number string (10, 20, 30, etc.), use that percentage for both front and back
                const numericValue = Number(dtiRatio)
                if (!isNaN(numericValue) && numericValue >= 10 && numericValue <= 80) {
                    const ratio = numericValue / 100
                    return { frontEnd: ratio, backEnd: ratio }
                }
                // Default to conventional
                return { frontEnd: 0.28, backEnd: 0.36 }
        }
    }, [dtiRatio])

    // Calculate monthly income
    const monthlyIncome = useMemo(() => {
        return debouncedAnnualIncome / 12
    }, [debouncedAnnualIncome])

    // Calculate maximum monthly housing payment based on front-end DTI
    const maxMonthlyHousingPayment = useMemo(() => {
        return monthlyIncome * dtiLimits.frontEnd
    }, [monthlyIncome, dtiLimits.frontEnd])

    // Calculate maximum total monthly debt payment based on back-end DTI
    const maxTotalMonthlyDebt = useMemo(() => {
        return monthlyIncome * dtiLimits.backEnd
    }, [monthlyIncome, dtiLimits.backEnd])

    // Calculate maximum monthly payment available for housing (after existing debt)
    const maxAvailableForHousing = useMemo(() => {
        const maxTotal = maxTotalMonthlyDebt
        const available = maxTotal - debouncedMonthlyDebt
        return Math.max(0, Math.min(available, maxMonthlyHousingPayment))
    }, [maxTotalMonthlyDebt, debouncedMonthlyDebt, maxMonthlyHousingPayment])

    // Calculate maximum affordable house price
    // This is an iterative calculation because:
    // - House price affects property tax, HOA, insurance (if percentage-based)
    // - House price affects down payment (if percentage-based)
    // - House price affects loan amount
    // - Loan amount affects monthly mortgage payment
    const affordabilityCalculation = useMemo(() => {
        if (debouncedAnnualIncome <= 0 || debouncedInterestRate < 0 || debouncedLoanTerm <= 0) {
            return {
                maxHousePrice: 0,
                maxLoanAmount: 0,
                downPaymentAmount: 0,
                monthlyMortgagePayment: 0,
                monthlyPropertyTax: 0,
                monthlyHoaFee: 0,
                monthlyInsurance: 0,
                totalMonthlyPayment: 0,
                fhaUpfrontInsurancePremium: 0,
                monthlyMIP: 0,
                estimatedClosingCost: 0,
                annualMaintenanceCost: 0,
                monthlyMaintenanceCost: 0,
                totalOneTimePayment: 0,
                totalMonthlyCost: 0,
                annualPropertyTax: 0,
                annualHoaFee: 0,
                annualInsurance: 0,
            }
        }

        // Use binary search to find maximum affordable house price
        let low = 0
        let high = monthlyIncome * 100 // Reasonable upper bound
        let bestResult = {
            maxHousePrice: 0,
            maxLoanAmount: 0,
            downPaymentAmount: 0,
            monthlyMortgagePayment: 0,
            monthlyPropertyTax: 0,
            monthlyHoaFee: 0,
            monthlyInsurance: 0,
            totalMonthlyPayment: 0,
            fhaUpfrontInsurancePremium: 0,
            monthlyMIP: 0,
            estimatedClosingCost: 0,
            annualMaintenanceCost: 0,
            monthlyMaintenanceCost: 0,
            totalOneTimePayment: 0,
            totalMonthlyCost: 0,
            annualPropertyTax: 0,
            annualHoaFee: 0,
            annualInsurance: 0,
        }

        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12

        for (let iteration = 0; iteration < 100; iteration++) {
            const testHousePrice = (low + high) / 2

            // Calculate down payment
            const downPaymentAmount = downPaymentMode === "percentage"
                ? (testHousePrice * debouncedDownPayment) / 100
                : debouncedDownPayment

            // Calculate loan amount
            const loanAmount = Math.max(0, testHousePrice - downPaymentAmount)

            if (loanAmount <= 0) {
                high = testHousePrice
                continue
            }

            // Calculate monthly mortgage payment
            let monthlyMortgagePayment = 0
            if (monthlyRate === 0) {
                monthlyMortgagePayment = loanAmount / numberOfPayments
            } else {
                monthlyMortgagePayment =
                    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
                    (Math.pow(1 + monthlyRate, numberOfPayments) - 1)
            }

            // Calculate monthly property tax
            const monthlyPropertyTax = propertyTaxMode === "percentage"
                ? (testHousePrice * debouncedPropertyTax) / 100 / 12
                : debouncedPropertyTax / 12

            // Calculate monthly HOA fee
            const monthlyHoaFee = hoaFeeMode === "percentage"
                ? (testHousePrice * debouncedHoaFee) / 100 / 12
                : debouncedHoaFee / 12

            // Calculate monthly insurance
            const monthlyInsurance = insuranceMode === "percentage"
                ? (testHousePrice * debouncedInsurance) / 100 / 12
                : debouncedInsurance / 12

            // Calculate total monthly payment
            const totalMonthlyPayment = monthlyMortgagePayment + monthlyPropertyTax + monthlyHoaFee + monthlyInsurance

            // Check if this fits within the budget
            if (totalMonthlyPayment <= maxAvailableForHousing && totalMonthlyPayment > 0) {
                // Calculate additional costs for this test price
                const testFhaUpfrontInsurancePremium = dtiRatio === "fha" ? loanAmount * 0.0175 : 0
                const testMonthlyMIP = dtiRatio === "fha" ? loanAmount * 0.005 / 12 : 0
                const testEstimatedClosingCost = testHousePrice * 0.03
                const testAnnualMaintenanceCost = testHousePrice * 0.015
                const testMonthlyMaintenanceCost = testAnnualMaintenanceCost / 12
                const testTotalOneTimePayment = downPaymentAmount + testFhaUpfrontInsurancePremium + testEstimatedClosingCost
                const testTotalMonthlyCost = totalMonthlyPayment + testMonthlyMIP + testMonthlyMaintenanceCost
                const testAnnualPropertyTax = monthlyPropertyTax * 12
                const testAnnualHoaFee = monthlyHoaFee * 12
                const testAnnualInsurance = monthlyInsurance * 12

                bestResult = {
                    maxHousePrice: testHousePrice,
                    maxLoanAmount: loanAmount,
                    downPaymentAmount: downPaymentAmount,
                    monthlyMortgagePayment: monthlyMortgagePayment,
                    monthlyPropertyTax: monthlyPropertyTax,
                    monthlyHoaFee: monthlyHoaFee,
                    monthlyInsurance: monthlyInsurance,
                    totalMonthlyPayment: totalMonthlyPayment,
                    fhaUpfrontInsurancePremium: testFhaUpfrontInsurancePremium,
                    monthlyMIP: testMonthlyMIP,
                    estimatedClosingCost: testEstimatedClosingCost,
                    annualMaintenanceCost: testAnnualMaintenanceCost,
                    monthlyMaintenanceCost: testMonthlyMaintenanceCost,
                    totalOneTimePayment: testTotalOneTimePayment,
                    totalMonthlyCost: testTotalMonthlyCost,
                    annualPropertyTax: testAnnualPropertyTax,
                    annualHoaFee: testAnnualHoaFee,
                    annualInsurance: testAnnualInsurance,
                }
                low = testHousePrice
            } else {
                high = testHousePrice
            }

            // Break if we're close enough
            if (high - low < 1) {
                break
            }
        }

        // All values are already calculated in bestResult during the loop
        return bestResult
    }, [
        debouncedAnnualIncome,
        debouncedInterestRate,
        debouncedLoanTerm,
        debouncedMonthlyDebt,
        debouncedDownPayment,
        debouncedPropertyTax,
        debouncedHoaFee,
        debouncedInsurance,
        downPaymentMode,
        propertyTaxMode,
        hoaFeeMode,
        insuranceMode,
        maxAvailableForHousing,
        monthlyIncome,
        dtiRatio,
    ])

    // Ref to store handleChange function for housePrice field
    const housePriceHandleChangeRef = useRef<((name: string, value: any) => void) | null>(null)

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.annualIncome !== undefined) setAnnualIncome(Number(data.annualIncome))
        if (data.loanTerm !== undefined) setLoanTerm(Number(data.loanTerm))
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.monthlyDebt !== undefined) setMonthlyDebt(Number(data.monthlyDebt))
        if (data.downPayment !== undefined) setDownPayment(Number(data.downPayment))
        if (data.downPayment_mode !== undefined) setDownPaymentMode(data.downPayment_mode)
        if (data.propertyTax !== undefined) setPropertyTax(Number(data.propertyTax))
        if (data.propertyTax_mode !== undefined) setPropertyTaxMode(data.propertyTax_mode)
        if (data.hoaFee !== undefined) setHoaFee(Number(data.hoaFee))
        if (data.hoaFee_mode !== undefined) setHoaFeeMode(data.hoaFee_mode)
        if (data.insurance !== undefined) setInsurance(Number(data.insurance))
        if (data.insurance_mode !== undefined) setInsuranceMode(data.insurance_mode)
        if (data.dtiRatio !== undefined) setDtiRatio(data.dtiRatio)
        // Note: housePrice is calculated and updated programmatically, not from form input
    }

    // Update housePrice in formData when calculation changes (without remounting)
    useEffect(() => {
        if (housePriceHandleChangeRef.current) {
            // Update housePrice field using handleChange - this updates formData without remounting
            housePriceHandleChangeRef.current('housePrice', affordabilityCalculation.maxHousePrice)
        }
    }, [affordabilityCalculation.maxHousePrice])

    const steps: Step[] = [
        {
            id: "income-and-debt",
            title: "Income & Debt",
            description: "Enter your annual income and monthly debt obligations",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "annualIncome",
                    label: "Annual household income",
                    placeholder: "120000",
                    required: true,
                    minValue: 0,
                    maxValue: 100000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    description: "salary + other incomes (before tax)",
                    laravelValidationRules: {
                        annualIncome: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTerm",
                    label: "Mortgage loan term",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "interestRate",
                    label: "Interest rate %",
                    placeholder: "6.317",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    allowNegative: false,
                    incrementValues: [1, 0.1, 0.01],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "monthlyDebt",
                    label: "Monthly debt payback",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 10000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    description: "long-term debts, car, student loan, etc",
                    laravelValidationRules: {
                        monthlyDebt: 'nullable|numeric|min:0'
                    }
                },
            ]
        },
        {
            id: "down-payment-and-costs",
            title: "Down Payment & Costs",
            description: "Enter down payment and annual costs as percentage or dollar amount",
            fields: [
                {
                    type: "numberInputPercentageValue",
                    name: "downPayment",
                    label: "Down payment",
                    placeholder: "20",
                    required: true,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        downPayment: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "propertyTax",
                    label: "Property tax",
                    placeholder: "1.5",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    laravelValidationRules: {
                        propertyTax: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "hoaFee",
                    label: "HOA or co-op fee",
                    placeholder: "0",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    laravelValidationRules: {
                        hoaFee: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "insurance",
                    label: "Insurance",
                    placeholder: "0.5",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    laravelValidationRules: {
                        insurance: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "select",
                    name: "dtiRatio",
                    label: "Debt-to-income (DTI) ratio",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: dtiOptions
                    },
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        dtiRatio: 'required|string'
                    }
                },
                {
                    type: "reactNode",
                    name: "housePrice",
                    label: "House Price (calculated)",
                    reactNode: null, // Will be set by custom renderer
                    ui: { colSpan: 12 },
                    hidden: true,
                    // Custom renderer that auto-updates housePrice
                    render: ({ handleChange, formData }: { handleChange: (name: string, value: any) => void; formData: Record<string, any> }) => {
                        // Store handleChange so we can use it to update housePrice
                        housePriceHandleChangeRef.current = handleChange

                        // Update housePrice if it's different from calculated value
                        const currentHousePrice = formData?.housePrice || 0
                        const calculatedHousePrice = affordabilityCalculation.maxHousePrice
                        if (Math.abs(currentHousePrice - calculatedHousePrice) > 0.01) {
                            // Use setTimeout to avoid updating during render
                            setTimeout(() => {
                                if (housePriceHandleChangeRef.current) {
                                    housePriceHandleChangeRef.current('housePrice', calculatedHousePrice)
                                }
                            }, 0)
                        }

                        return null // Hidden field, no UI
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">House Affordability Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate how much house you can afford based on your income, debt, and expenses
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            annualIncome: annualIncome,
                            loanTerm: loanTerm,
                            interestRate: interestRate,
                            monthlyDebt: monthlyDebt,
                            downPayment: downPayment,
                            downPayment_mode: downPaymentMode,
                            propertyTax: propertyTax,
                            propertyTax_mode: propertyTaxMode,
                            hoaFee: hoaFee,
                            hoaFee_mode: hoaFeeMode,
                            insurance: insurance,
                            insurance_mode: insuranceMode,
                            dtiRatio: dtiRatio,
                            housePrice: affordabilityCalculation.maxHousePrice, // Add housePrice to formData for reference
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Summary */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <p className="text-base leading-relaxed">
                            You can afford a house up to <span className="font-bold text-primary text-lg">$<AnimatedNumber value={affordabilityCalculation.maxHousePrice} decimals={0} /></span> with {dtiRatio === "fha" ? "an FHA" : "a"} loan, within which <span className="font-bold">$<AnimatedNumber value={affordabilityCalculation.maxLoanAmount} decimals={0} /></span> is the loan and <span className="font-bold">$<AnimatedNumber value={affordabilityCalculation.downPaymentAmount} decimals={0} /></span> is the down payment.
                        </p>
                    </div>

                    {/* Borrowing and One-Time Payments */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Borrowing and One-Time Payments</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">You can borrow:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.maxLoanAmount} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total price of the house:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.maxHousePrice} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Down payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.downPaymentAmount} decimals={0} />
                                </span>
                            </div>
                            {dtiRatio === "fha" && (
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">FHA upfront insurance premium (1.75%):</span>
                                    <span className="text-sm font-semibold">
                                        $<AnimatedNumber value={affordabilityCalculation.fhaUpfrontInsurancePremium} decimals={0} />
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Estimated closing cost (one time, assume 3%):</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.estimatedClosingCost} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Front-end debt-to-income (DTI) ratio:</span>
                                <span className="text-sm font-semibold">
                                    {Math.round(dtiLimits.frontEnd * 100)}%
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Back-end debt-to-income (DTI) ratio:</span>
                                <span className="text-sm font-semibold">
                                    {Math.round(dtiLimits.backEnd * 100)}%
                                </span>
                            </div>
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Total one-time payment at closing:</span>
                                <span className="text-base font-bold text-primary">
                                    $<AnimatedNumber value={affordabilityCalculation.totalOneTimePayment} decimals={0} />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Monthly and Annual Costs */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Monthly and Annual Costs</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Monthly mortgage payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.monthlyMortgagePayment} decimals={0} />
                                </span>
                            </div>
                            {dtiRatio === "fha" && (
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Monthly MIP payment (0.5%):</span>
                                    <span className="text-sm font-semibold">
                                        $<AnimatedNumber value={affordabilityCalculation.monthlyMIP} decimals={0} />
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Annual property tax:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.annualPropertyTax} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Annual HOA or co-op fee:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.annualHoaFee} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Annual insurance cost:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.annualInsurance} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Estimated annual maintenance cost (repair, utility etc., assume 1.5%):</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.annualMaintenanceCost} decimals={0} />
                                </span>
                            </div>
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Total monthly cost on the house:</span>
                                <span className="text-base font-bold text-primary">
                                    $<AnimatedNumber value={affordabilityCalculation.totalMonthlyCost} decimals={0} />
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    )
}

